import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Save, RotateCcw, UserCog, Users2, MapPin, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner } from "../../components/common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ModuleAccessEditor, LEVEL_BADGE, MODULE_LEVELS, FEATURE_LEVELS } from "../../components/ModuleAccessEditor";
import { ManageRolesDialog } from "../../components/ManageRolesDialog";   // Phase 174
import { TerritoryDialog } from "../../components/TerritoryDialog";         // Phase 174B: map a new role's zones / wards in place
import { api, errMsg } from "../../lib/api";

const LEVELS = MODULE_LEVELS;

// Phase 152: the server returns EFFECTIVE maps (module keys + "module.feature" keys). For editing, feature keys that are
// not explicitly configured are shown as "inherit" (= Same as module).
const toEditable = (effective, configuredKeys) => {
  const set = new Set(configuredKeys || []);
  const out = {};
  Object.entries(effective || {}).forEach(([k, v]) => { out[k] = k.includes(".") && !set.has(k) ? "inherit" : v; });
  return out;
};

export default function AccessControl() {
  const [munis, setMunis] = useState(null);
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);        // role-wise config
  const [config, setConfig] = useState({});      // editable {role: {module: level}}
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  // user-wise
  const [users, setUsers] = useState(null);
  const [userId, setUserId] = useState("");
  const [userData, setUserData] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [userDirty, setUserDirty] = useState(false);
  // Phase 174: the roles this person holds (one login identity, a linked account per role)
  const [personRoles, setPersonRoles] = useState(null);
  const [rolesOpen, setRolesOpen] = useState(false);
  // Phase 174B: "Map territory now" — the role account being mapped + the ones still waiting (two roles added at once)
  const [territoryTarget, setTerritoryTarget] = useState(null);
  const [territoryQueue, setTerritoryQueue] = useState([]);

  const loadPersonRoles = useCallback((uid) => {
    if (!code || !uid) { setPersonRoles(null); return; }
    api.get(`/t/${code}/users/${uid}/roles`)
      .then((r) => setPersonRoles(r.data))
      .catch(() => setPersonRoles(null));   // non-staff / sub-user accounts simply hide the roles row
  }, [code]);

  // Open the territory dialog for the first role account; queue the rest.
  const startTerritoryMapping = useCallback((accounts) => {
    if (!accounts?.length) return;
    const [first, ...rest] = accounts;
    setTerritoryQueue(rest);
    setTerritoryTarget(first);
  }, []);

  // After one role is mapped, offer the next queued role (if any) and refresh the badges.
  const onTerritorySaved = useCallback(() => {
    loadPersonRoles(userId);
    if (territoryQueue.length) {
      const [next, ...rest] = territoryQueue;
      setTerritoryQueue(rest);
      toast.info(`${next.label || next.role.replace(/_/g, " ")} role still has no territory`, {
        duration: 12000,
        action: { label: "Map territory now", onClick: () => setTerritoryTarget(next) },
      });
    }
  }, [loadPersonRoles, userId, territoryQueue]);

  const territoryLabel = (t) => {
    if (!t) return null;
    if (!t.mapped) return "no territory";
    const bits = [];
    if (t.zones) bits.push(`${t.zones} zone${t.zones === 1 ? "" : "s"}`);
    if (t.wards) bits.push(`${t.wards} ward${t.wards === 1 ? "" : "s"}`);
    if (t.mohallas) bits.push(`${t.mohallas} mohalla${t.mohallas === 1 ? "" : "s"}`);
    return bits.join(", ");
  };

  useEffect(() => {
    api.get("/platform/municipalities")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data.items || [];
        setMunis(list);
        if (list.length > 0) setCode(list[0].code);
      })
      .catch((e) => { toast.error(errMsg(e)); setMunis([]); });
  }, []);

  const loadRoleConfig = useCallback(() => {
    if (!code) return;
    setData(null);
    api.get(`/t/${code}/role-modules`)
      .then((r) => {
        setData(r.data);
        setConfig(Object.fromEntries(Object.entries(r.data.config).map(([role, eff]) => [role, toEditable(eff, r.data.configured?.[role])])));
        setDirty(false);
      })
      .catch((e) => { toast.error(errMsg(e)); setData({ registry: [], config: {} }); });
  }, [code]);

  const loadUsers = useCallback(() => {
    if (!code) return;
    setUsers(null);
    setUserId("");
    setUserData(null);
    api.get(`/t/${code}/role-modules/users`)
      .then((r) => setUsers(r.data))
      .catch((e) => { toast.error(errMsg(e)); setUsers([]); });
  }, [code]);

  useEffect(() => { loadRoleConfig(); loadUsers(); }, [loadRoleConfig, loadUsers]);

  useEffect(() => {
    if (!userId) { setUserData(null); setPersonRoles(null); return; }
    setUserData(null);
    setPersonRoles(null);
    setTerritoryQueue([]);
    setTerritoryTarget(null);
    loadPersonRoles(userId);
    api.get(`/t/${code}/role-modules/user/${userId}`)
      .then((r) => {
        setUserData(r.data);
        const init = {};
        r.data.registry.forEach((m) => {
          init[m.key] = r.data.overrides[m.key] || "inherit";
          (m.features || []).forEach((f) => { const k = `${m.key}.${f.key}`; init[k] = r.data.overrides[k] || "inherit"; });
        });
        setOverrides(init);
        setUserDirty(false);
      })
      .catch((e) => toast.error(errMsg(e)));
  }, [code, userId, loadPersonRoles]);

  const setLevel = (role, key, level) => {
    setConfig((c) => ({ ...c, [role]: { ...c[role], [key]: level } }));
    setDirty(true);
  };

  const saveRoles = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/t/${code}/role-modules`, { config });
      setConfig(Object.fromEntries(Object.entries(r.data.config).map(([role, eff]) => [role, toEditable(eff, r.data.configured?.[role])])));
      setDirty(false);
      toast.success("Role-wise access saved — applies to menus and APIs immediately");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveUser = async () => {
    setBusy(true);
    try {
      const body = {};
      Object.entries(overrides).forEach(([k, v]) => { if (v !== "inherit") body[k] = v; });
      const r = await api.put(`/t/${code}/role-modules/user/${userId}`, { overrides: body });
      toast.success(`Access overrides saved for ${userData?.user?.name}`);
      setUserDirty(false);
      setUserData((d) => ({ ...d, effective: r.data.effective, overrides: r.data.overrides }));
      loadUsers();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const muni = (munis || []).find((m) => m.code === code);

  return (
    <div className="space-y-4">
      <PageHeader title="Access Control" subtitle="Role-wise and user-wise menu access with view / edit / full permissions — for every municipality on the platform">
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Municipality</Label>
            <Select value={code} onValueChange={setCode}>
              <SelectTrigger className="h-9 w-64" data-testid="access-muni-select"><SelectValue placeholder="Select municipality" /></SelectTrigger>
              <SelectContent>
                {(munis || []).map((m) => <SelectItem key={m.code} value={m.code}>{m.name} ({m.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={() => { loadRoleConfig(); loadUsers(); }} data-testid="access-refresh-button">
            <RotateCcw className="h-4 w-4 mr-1.5" /> Reload
          </Button>
        </div>
      </PageHeader>

      {!munis ? <Spinner /> : munis.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">No municipalities yet.</CardContent></Card>
      ) : (
        <Tabs defaultValue="roles">
          <TabsList>
            <TabsTrigger value="roles" data-testid="access-tab-roles"><ShieldCheck className="h-4 w-4 mr-1.5" /> Role-wise</TabsTrigger>
            <TabsTrigger value="users" data-testid="access-tab-users"><UserCog className="h-4 w-4 mr-1.5" /> User-wise</TabsTrigger>
          </TabsList>

          {/* ---------------- ROLE-WISE ---------------- */}
          <TabsContent value="roles" className="space-y-4 mt-4">
            {!data ? <Spinner /> : (
              <>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span>Levels:</span>
                  {LEVELS.map((l) => (
                    <Badge key={l.value} variant="outline" className={`text-[11px] ${LEVEL_BADGE[l.value]}`}>{l.label}</Badge>
                  ))}
                  <span className="ml-1">• No access hides the menu and blocks its APIs • View allows read-only • Edit allows updates but not create/delete</span>
                  <span className="ml-2">Sub-features:</span>
                  {FEATURE_LEVELS.map((l) => (
                    <Badge key={l.value} variant="outline" className={`text-[11px] ${LEVEL_BADGE[l.value]}`}>{l.label}</Badge>
                  ))}
                  <Badge variant="outline" className="text-[11px]">Same as module</Badge>
                  <span className="ml-1">• Expand a module to control each sub-tab / feature — never above the module</span>
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  {data.registry.map((r) => (
                    <Card key={r.role} className="shadow-sm" data-testid={`access-role-card-${r.role}`}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base font-heading flex items-center justify-between">
                          {r.role_label}
                          {r.role === "municipal_admin" && <Badge variant="outline" className="text-[10px]">Super Admin only</Badge>}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {/* Phase 152: module level + expandable sub-feature levels */}
                        <ModuleAccessEditor modules={r.modules} mode="role" getValue={(k) => config[r.role]?.[k]}
                          onChange={(k, v) => setLevel(r.role, k, v)} testId={`access-${r.role}`} />
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <div className="sticky bottom-4 flex justify-end">
                  <Button size="lg" onClick={saveRoles} disabled={!dirty || busy} data-testid="access-save-roles-button">
                    <Save className="h-4 w-4 mr-2" /> {busy ? "Saving..." : `Save role access for ${muni?.short_name || code}`}
                  </Button>
                </div>
              </>
            )}
          </TabsContent>

          {/* ---------------- USER-WISE ---------------- */}
          <TabsContent value="users" className="space-y-4 mt-4">
            {!users ? <Spinner /> : (
              <>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">User</Label>
                    <Select value={userId} onValueChange={setUserId}>
                      <SelectTrigger className="h-10 w-96" data-testid="access-user-select"><SelectValue placeholder="Select a user to customise" /></SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name} — {u.role.replace("_", " ")} {u.has_overrides ? "• customised" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground pb-2">
                    Overrides win over the role default for that person only. "Inherit" follows the role setting.
                    Use <span className="font-medium text-foreground">Manage roles</span> to give one person several roles (one sign-in).
                  </p>
                </div>
                {userId && !userData && <Spinner />}
                {userData && (
                  <Card className="shadow-sm" data-testid="access-user-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base font-heading">
                        {userData.user.name} <span className="text-sm font-normal text-muted-foreground">({userData.user.email} • {userData.user.role.replace("_", " ")})</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {/* Phase 174: roles this person holds (one sign-in, a linked account per role) */}
                      {personRoles && (
                        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2.5 mb-3" data-testid="access-user-roles-row">
                          <Users2 className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-xs text-muted-foreground">Roles held:</span>
                          {personRoles.roles.filter((r) => r.is_active).map((r) => {
                            const unmapped = r.territory && !r.territory.mapped;
                            const terr = territoryLabel(r.territory);
                            return (
                              <span key={r.user_id} className="inline-flex items-center gap-1">
                                <Badge variant="outline"
                                  className={`text-[11px] ${unmapped ? "border-amber-300 bg-amber-50 text-amber-900" : r.user_id === userId ? "border-primary/50 bg-primary/5 text-primary" : ""}`}
                                  data-testid={`access-user-role-badge-${r.role}`} data-territory={r.territory ? (unmapped ? "unmapped" : "mapped") : "n/a"}>
                                  {unmapped && <AlertTriangle className="h-3 w-3 mr-1" aria-hidden="true" />}
                                  {r.label}{r.user_id === userId ? " · selected" : ""}{terr ? ` · ${terr}` : ""}
                                </Badge>
                                {r.territory && (
                                  <button type="button"
                                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${unmapped ? "text-amber-700" : "text-muted-foreground"}`}
                                    title={unmapped ? `Map territory for the ${r.label} role` : `Edit ${r.label} territory`}
                                    aria-label={`${unmapped ? "Map" : "Edit"} ${r.label} territory`}
                                    onClick={() => startTerritoryMapping([{ id: r.user_id, name: personRoles.name, employee_id: r.employee_id, role: r.role, label: r.label }])}
                                    data-testid={`access-user-role-territory-${r.role}`}>
                                    <MapPin className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </span>
                            );
                          })}
                          {personRoles.roles.filter((r) => !r.is_active).map((r) => (
                            <Badge key={r.user_id} variant="outline" className="text-[11px] text-muted-foreground line-through" data-testid={`access-user-role-badge-${r.role}`}>
                              {r.label}
                            </Badge>
                          ))}
                          <Button variant="outline" size="sm" className="ml-auto h-8" onClick={() => setRolesOpen(true)} data-testid="access-manage-roles-button">
                            <UserCog className="h-4 w-4 mr-1.5" /> Manage roles
                          </Button>
                        </div>
                      )}
                      <ModuleAccessEditor modules={userData.registry} mode="user" baseline={userData.role_defaults}
                        getValue={(k) => overrides[k]} onChange={(k, v) => { setOverrides((o) => ({ ...o, [k]: v })); setUserDirty(true); }}
                        testId="access-user" />
                      <div className="flex justify-end pt-2">
                        <Button onClick={saveUser} disabled={!userDirty || busy} data-testid="access-save-user-button">
                          <Save className="h-4 w-4 mr-2" /> {busy ? "Saving..." : "Save user overrides"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
                {/* Phase 174: assign / remove roles for the selected person */}
                {userId && (
                  <ManageRolesDialog code={code} userId={userId} open={rolesOpen} onOpenChange={setRolesOpen}
                    onMapTerritory={startTerritoryMapping}
                    onSaved={(d) => {
                      setPersonRoles(d);
                      // the user list gains / loses role accounts — refresh it without losing the current selection
                      api.get(`/t/${code}/role-modules/users`).then((r) => setUsers(r.data)).catch(() => {});
                    }} />
                )}
                {/* Phase 174B: "Map territory now" — the same dialog the Municipal Admin uses in Users & Staff */}
                {territoryTarget && (
                  <TerritoryDialog code={code} user={territoryTarget} open={!!territoryTarget}
                    onOpenChange={(v) => { if (!v) setTerritoryTarget(null); }}
                    onSaved={onTerritorySaved} />
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
